import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import RichEditor from "../components/RichEditor";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { FiEdit2, FiTrash2, FiPlus, FiX, FiImage, FiExternalLink } from "react-icons/fi";

const emptyForm = { title: "", category: "", excerpt: "", content: "", coverImage: "" };

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const snap = await getDocs(query(collection(db, "blogs"), orderBy("createdAt", "desc")));
      setBlogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      const catSnap = await getDocs(collection(db, "blogCategories"));
      setCategories(catSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
      setForm((f) => ({ ...f, coverImage: base64 }));
    } catch (e) { alert("Upload failed."); }
    setUploading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await updateDoc(doc(db, "blogs", editId), { ...form, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "blogs"), { ...form, createdAt: serverTimestamp() });
      }
      setShowForm(false); setForm(emptyForm); setEditId(null);
      fetchData();
    } catch (e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const handleEdit = (blog) => {
    setForm({ title: blog.title || "", category: blog.category || "", excerpt: blog.excerpt || "", content: blog.content || "", coverImage: blog.coverImage || "" });
    setEditId(blog.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    await deleteDoc(doc(db, "blogs", id));
    fetchData();
  };

  const closeForm = () => { setShowForm(false); setForm(emptyForm); setEditId(null); };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-black">Blog Posts</h1>
            <p className="text-sm text-gray-400">Manage your blog content</p>
          </div>
          <button onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#e84fa5] text-white font-bold uppercase text-[13px] rounded-lg hover:bg-pink-600 transition">
            <FiPlus /> Add Post
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin"></div></div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Post</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Category</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Date</th>
                  <th className="text-right px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-10 text-gray-400 text-sm">No blog posts yet. Create your first post!</td></tr>
                )}
                {blogs.map((b) => (
                  <tr key={b.id} className="border-b hover:bg-gray-50 transition">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {b.coverImage && <img src={b.coverImage} alt="" className="w-10 h-10 object-cover rounded-lg" />}
                        <span className="font-semibold text-[13px] text-black line-clamp-1">{b.title}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4"><span className="px-2 py-1 bg-pink-50 text-[#e84fa5] text-[11px] font-bold rounded-full">{b.category || "—"}</span></td>
                    <td className="px-5 py-4 text-[12px] text-gray-400">
                      {b.createdAt?.toDate ? b.createdAt.toDate().toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a href={`/blog/${b.id}`} target="_blank" rel="noopener noreferrer" className="p-2 text-green-500 hover:bg-green-50 rounded-lg" title="View"><FiExternalLink size={14} /></a>
                        <button onClick={() => handleEdit(b)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"><FiEdit2 size={14} /></button>
                        <button onClick={() => handleDelete(b.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><FiTrash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* FORM MODAL */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-6 px-4">
            <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl">
              <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10 rounded-t-2xl">
                <h2 className="text-lg font-extrabold">{editId ? "Edit Post" : "New Blog Post"}</h2>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-700"><FiX size={22} /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-5">
                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-2">Cover Image</label>
                  {form.coverImage && (
                    <div className="relative inline-block mb-3">
                      <img src={form.coverImage} alt="" className="h-28 w-auto rounded-xl border object-cover" />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, coverImage: "" }))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"><FiX /></button>
                    </div>
                  )}
                  <label className="inline-flex items-center gap-2 cursor-pointer px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
                    <FiImage /> {uploading ? "Uploading..." : "Upload Cover Image"}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Post Title *</label>
                    <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[14px] outline-none focus:border-[#e84fa5]" placeholder="Post title" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Category</label>
                    <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[14px] outline-none focus:border-[#e84fa5] bg-white">
                      <option value="">Select category</option>
                      {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Excerpt (short summary)</label>
                  <textarea value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} rows={2}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-none" placeholder="Short preview text shown in blog list..." />
                </div>
                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-2">Content *</label>
                  <RichEditor value={form.content} onChange={(val) => setForm((f) => ({ ...f, content: val }))} placeholder="Write your blog post here..." />
                </div>
                <div className="flex justify-end gap-3 pt-2 border-t">
                  <button type="button" onClick={closeForm}
                    className="px-6 py-2.5 border border-gray-200 rounded-lg text-[13px] font-semibold hover:bg-gray-50 transition">Cancel</button>
                  <button type="submit" disabled={saving || uploading}
                    className="px-8 py-2.5 bg-[#e84fa5] text-white font-bold text-[13px] rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
                    {saving ? "Saving..." : editId ? "Update Post" : "Publish Post"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Blogs;
